import React, { FC, useEffect, useState } from 'react'
import { Button, ConfigProvider, Drawer, Flex, Form, InputNumber, Segmented, Select, Space, Typography } from 'antd'
import { useService } from '@pushly/aqe/lib/hooks/use-service'
import { PageAnalysisRuleState, PageAnalysisScope, UnknownContentAgePolicy } from '@pushly/models/lib/enums'
import { RelativeDateDisplayMetric } from '@pushly/models/lib/enums/relative-date-display-metric'
import {
    RULE_STATE_DESCRIPTIONS,
    RULE_STATE_LABELS,
    SCOPE_LABELS,
    SECONDS_PER_METRIC,
    UNKNOWN_AGE_POLICY_LABELS,
} from './constants'
import { DEFAULT_DRAWER_FORM_ITEM_LAYOUT } from '../../constants'
import { PageAnalysisGateService } from '../../services/page-analysis-gate.service'
import { IPageAnalysisContentAgeLimit } from '../../interfaces/page-analysis-gates'
import { simpleNotification } from '../../_utils/utils'

interface IGateAgeLimitDrawer {
    open: boolean
    domainId?: number
    scope: PageAnalysisScope
    /** The limit in force for this scope, if there is one. Absent means setting a first one. */
    limit?: IPageAnalysisContentAgeLimit
    onClose: () => void
    onSaved: () => void
}

const METRIC_OPTIONS = Object.keys(SECONDS_PER_METRIC).map((metric) => ({
    value: metric as RelativeDateDisplayMetric,
    label: metric,
}))

/**
 * Mirrors sailfish's MAX_CONTENT_AGE_SECONDS. Past it the interval overflows the
 * timedelta page-analyzer holds it in, so the API rejects the whole request. The
 * bound is enforced here as well to keep that out of a save the operator has
 * already filled in.
 */
const MAX_AGE_SECONDS = 100 * 365 * 24 * 60 * 60

const secondsPerUnit = (metric: RelativeDateDisplayMetric): number =>
    SECONDS_PER_METRIC[metric] ?? SECONDS_PER_METRIC[RelativeDateDisplayMetric.DAYS]!

const STATE_OPTIONS = [PageAnalysisRuleState.DISABLED, PageAnalysisRuleState.TRACE, PageAnalysisRuleState.ACTIVE].map(
    (state) => ({ value: state, label: RULE_STATE_LABELS[state] }),
)

const UNKNOWN_OPTIONS = Object.values(UnknownContentAgePolicy).map((policy) => ({
    value: policy,
    label: UNKNOWN_AGE_POLICY_LABELS[policy],
}))

export const GateAgeLimitDrawer: FC<IGateAgeLimitDrawer> = ({ open, domainId, scope, limit, onClose, onSaved }) => {
    const gateService = useService<PageAnalysisGateService>(PageAnalysisGateService)

    const [amount, setAmount] = useState<number>(90)
    const [metric, setMetric] = useState<RelativeDateDisplayMetric>(RelativeDateDisplayMetric.DAYS)
    const [unknownPolicy, setUnknownPolicy] = useState<UnknownContentAgePolicy>(UnknownContentAgePolicy.ANALYZE)
    const [state, setState] = useState<PageAnalysisRuleState>(PageAnalysisRuleState.TRACE)
    const [saving, setSaving] = useState(false)

    const maxAmount = Math.floor(MAX_AGE_SECONDS / secondsPerUnit(metric))

    // Switching to a coarser unit lowers the ceiling, so the amount already in
    // the field can land above it.
    const changeMetric = (next: RelativeDateDisplayMetric) => {
        setMetric(next)
        setAmount(Math.min(amount, Math.floor(MAX_AGE_SECONDS / secondsPerUnit(next))))
    }

    // A PUT replaces the limit, so editing one has to start from what is stored.
    // Opening on the defaults would quietly rewrite every field the operator did
    // not look at.
    useEffect(() => {
        if (!open) {
            return
        }

        if (!limit) {
            setAmount(90)
            setMetric(RelativeDateDisplayMetric.DAYS)
            setUnknownPolicy(UnknownContentAgePolicy.ANALYZE)
            setState(PageAnalysisRuleState.TRACE)
            return
        }

        // The metric records the unit the interval was authored in. It is
        // optional on the API, and an unrecognized one would divide by nothing.
        const storedMetric =
            limit.maxAgeDisplayMetric && SECONDS_PER_METRIC[limit.maxAgeDisplayMetric]
                ? limit.maxAgeDisplayMetric
                : RelativeDateDisplayMetric.DAYS

        setAmount(Math.max(1, Math.round(limit.maxAgeSeconds / secondsPerUnit(storedMetric))))
        setMetric(storedMetric)
        setUnknownPolicy(limit.unknownPolicy)
        setState(limit.state)
    }, [open, limit])

    const submit = async () => {
        if (!domainId) {
            return
        }

        setSaving(true)

        const res = await gateService.putContentAgeLimit(domainId, scope, {
            maxAgeSeconds: amount * secondsPerUnit(metric),
            maxAgeDisplayMetric: metric,
            unknownPolicy,
            state,
        })

        setSaving(false)

        // aqe reports failure through `ok`; `error` is never populated.
        if (!res.ok) {
            simpleNotification('error', 'The age limit could not be saved.')
            return
        }

        onSaved()
        onClose()
    }

    return (
        <Drawer
            title={(limit ? 'Edit the content age limit: ' : 'Content age limit: ') + SCOPE_LABELS[scope]}
            placement="right"
            width={600}
            open={open}
            onClose={onClose}
            destroyOnHidden={true}
            footer={
                <div style={{ textAlign: 'right' }}>
                    <Space>
                        <Button onClick={onClose}>Cancel</Button>
                        <Button
                            type="primary"
                            onClick={submit}
                            loading={saving}
                            disabled={!amount || amount > maxAmount}
                        >
                            Save limit
                        </Button>
                    </Space>
                </div>
            }
        >
            <ConfigProvider theme={{ components: { Form: { itemMarginBottom: 16 } } }}>
                <Form {...DEFAULT_DRAWER_FORM_ITEM_LAYOUT} labelAlign="left" requiredMark={false}>
                    <Form.Item label="Older than">
                        <Space.Compact>
                            <InputNumber
                                min={1}
                                max={maxAmount}
                                precision={0}
                                value={amount}
                                onChange={(next) => setAmount(next ?? 1)}
                            />
                            <Select
                                value={metric}
                                onChange={changeMetric}
                                options={METRIC_OPTIONS}
                                style={{ width: 140 }}
                            />
                        </Space.Compact>
                    </Form.Item>

                    <Form.Item label="No date">
                        <Flex vertical gap={4} align="flex-start">
                            <Select
                                value={unknownPolicy}
                                onChange={(next) => setUnknownPolicy(next)}
                                options={UNKNOWN_OPTIONS}
                                style={{ width: 220 }}
                            />
                            <Typography.Text className="ai-muted-text">
                                A missing date means opposite things on different domains. On one it marks a page that
                                was never an article; on one whose CMS omits the tag it is every article.
                            </Typography.Text>
                        </Flex>
                    </Form.Item>

                    <Form.Item label="State">
                        <Flex vertical gap={4} align="flex-start">
                            <Segmented
                                value={state}
                                onChange={(next) => setState(next as PageAnalysisRuleState)}
                                options={STATE_OPTIONS}
                            />
                            <Typography.Text className="ai-muted-text">
                                {RULE_STATE_DESCRIPTIONS[state]}
                            </Typography.Text>
                        </Flex>
                    </Form.Item>

                    <Form.Item label=" " colon={false}>
                        <Typography.Text className="ai-muted-text">
                            Older content is often the most embedded band in a library, so a limit trades away part of
                            the recommendation pool rather than removing waste. Leave it reporting until the numbers
                            agree.
                        </Typography.Text>
                    </Form.Item>
                </Form>
            </ConfigProvider>
        </Drawer>
    )
}
